import { useEffect, useMemo, useState } from 'react'

import { api } from '../api'
import { PresetNumber, Stepper } from '../components/NumberPick'
import { formatWhen, t } from '../i18n'
import { confirmAction, haptic, notify, useBackButton } from '../telegram'
import { isIndividualKind } from '../types'
import type { Club, Game, ScoreMode, Tournament, TSettings } from '../types'

interface Props {
  game: Game
  data: Tournament
  onClose: (changed: boolean) => void
}

const pad = (n: number) => String(n).padStart(2, '0')

/** «Редактировать турнир»: одна форма для организатора. До старта меняется
 *  всё; после старта — взнос, комментарий, длительность, «корт забронирован»,
 *  очки за матч (и число раундов у mexicano). Остальное видно, но заблокировано. */
export default function TournamentEditor({ game, data, onClose }: Props) {
  const before = data.status === 'open' || data.status === 'full'
  const mex = data.kind === 'mexicano'
  const king = data.kind === 'king'
  const start = new Date(game.starts_at)
  const joined = game.slots.filter((s) => s.status === 'joined').length

  const [clubs, setClubs] = useState<Club[]>([game.club])
  const [clubId, setClubId] = useState(game.club.id)
  const [date, setDate] = useState(`${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`)
  const [time, setTime] = useState(`${pad(start.getHours())}:${pad(start.getMinutes())}`)
  const [duration, setDuration] = useState(game.duration_min)
  const [booked, setBooked] = useState(game.court_booked)
  const [price, setPrice] = useState(game.price_per_player != null ? String(game.price_per_player) : '')
  const [notes, setNotes] = useState(game.notes ?? '')
  const [places, setPlaces] = useState(data.max_players)
  const [mode, setMode] = useState<ScoreMode>(data.score_mode)
  const [points, setPoints] = useState(data.points_per_match ?? (data.score_mode === 'points' ? 21 : 6))
  const [rounds, setRounds] = useState(data.rounds_setting ?? 0)
  const [courts, setCourts] = useState(data.courts_setting ?? 0)
  const [roundMinutes, setRoundMinutes] = useState(data.round_minutes ?? 12)
  const [busy, setBusy] = useState(false)

  useEffect(() => useBackButton(() => onClose(false)), [onClose])
  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [])
  useEffect(() => {
    if (before) api.clubs().then(setClubs, () => undefined)
  }, [before])

  const byCity = useMemo(() => {
    const groups = new Map<string, Club[]>()
    for (const c of clubs) groups.set(c.city, [...(groups.get(c.city) ?? []), c])
    return [...groups.entries()]
  }, [clubs])

  const pointsMode = isIndividualKind(data.kind) || mode === 'points'
  const roundsEditable = (isIndividualKind(data.kind) || king) && (before || ((mex || king) && data.status === 'running'))
  const step = data.kind === 'pairs' || king ? 2 : 1
  const minPlaces = Math.max(4, data.kind === 'pairs' || king ? joined + (joined % 2) : joined)

  const priceValue = price.trim() === '' ? null : Number(price.replace(',', '.'))
  const oldPrice = game.price_per_player ?? null
  const nextStart = new Date(`${date}T${time}:00`)
  const timeChanged = before && Math.abs(nextStart.getTime() - start.getTime()) >= 60_000
  const clubChanged = before && clubId !== game.club.id

  const settings: TSettings = {}
  if (!king && points !== data.points_per_match) settings.points_per_match = points
  if (king && roundMinutes !== (data.round_minutes ?? 12)) settings.round_minutes = roundMinutes
  if (priceValue !== oldPrice) settings.price_per_player = priceValue
  if (notes.trim() !== (game.notes ?? '')) settings.notes = notes.trim()
  if (duration !== game.duration_min) settings.duration_min = duration
  if (booked !== game.court_booked) settings.court_booked = booked
  if (roundsEditable && rounds !== (data.rounds_setting ?? 0)) settings.rounds = rounds
  if (before) {
    if (data.kind === 'pairs' && mode !== data.score_mode) settings.score_mode = mode
    if (places !== data.max_players) settings.max_players = places
    if (!king && courts !== (data.courts_setting ?? 0)) settings.courts = courts
  }
  const dirty = timeChanged || clubChanged || Object.keys(settings).length > 0

  async function save() {
    if (priceValue != null && (!Number.isFinite(priceValue) || priceValue < 0 || priceValue > 200)) {
      return notify(t().priceEuro)
    }
    if ((timeChanged || clubChanged) && Number.isNaN(nextStart.getTime())) return
    if (timeChanged || clubChanged) {
      const clubName = clubs.find((c) => c.id === clubId)?.name ?? game.club.name
      if (!(await confirmAction(t().changeGameConfirm(formatWhen(nextStart.toISOString()), clubName)))) return
    }
    setBusy(true)
    haptic('medium')
    try {
      if (Object.keys(settings).length) await api.tournamentSettings(game.id, settings)
      if (timeChanged || clubChanged) {
        await api.updateGame(game.id, {
          ...(timeChanged ? { starts_at: nextStart.toISOString() } : {}),
          ...(clubChanged ? { club_id: clubId } : {}),
        })
      }
      notify(t().editSaved)
      onClose(true)
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Ошибка')
    } finally {
      setBusy(false)
    }
  }

  const locked = !before && <div className="muted small">{t().lockedAfterStart}</div>

  return (
    <div className="chat-screen" role="dialog" aria-modal="true" aria-label={t().editTournament}>
      <div className="chat-head">
        <button className="ghost chat-back" onClick={() => onClose(false)} aria-label={t().back}>
          ←
        </button>
        <div className="title chat-title">{t().editTournament}</div>
      </div>
      <div className="chat-list">
        <div className="deal-form t-editor">
          <div className="title">{t().editSectionWhen}</div>
          {locked}
          <label>{t().club}</label>
          <select disabled={!before} value={clubId} onChange={(e) => setClubId(Number(e.target.value))}>
            {byCity.map(([city, list]) => (
              <optgroup key={city} label={city}>
                {list.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <div className="row" style={{ gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label>{t().date}</label>
              <input type="date" disabled={!before} value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label>{t().time}</label>
              <input type="time" disabled={!before} value={time} onChange={(e) => setTime(e.target.value)} />
            </div>
          </div>
          <label>{t().duration}</label>
          <PresetNumber
            value={duration}
            presets={[120, 180, 240]}
            min={30}
            max={600}
            unit={t().minutes}
            label={t().duration}
            onChange={setDuration}
          />
          <label>
            <span className="row" style={{ gap: 10 }}>
              <input type="checkbox" style={{ width: 20 }} checked={booked} onChange={(e) => setBooked(e.target.checked)} />
              {t().courtBooked}
            </span>
          </label>

          <div className="title" style={{ marginTop: 18 }}>
            {t().editSectionMoney}
          </div>
          <label>{t().priceEuro}</label>
          <input
            inputMode="decimal"
            placeholder={t().priceNone}
            value={price}
            onChange={(e) => setPrice(e.target.value.replace(/[^\d.,]/g, '').slice(0, 6))}
          />
          <label>{t().notes}</label>
          <textarea rows={3} maxLength={500} value={notes} onChange={(e) => setNotes(e.target.value)} />

          <div className="title" style={{ marginTop: 18 }}>
            {t().editSectionFormat}
          </div>
          <label>{t().placesLabel}</label>
          {before ? (
            <Stepper value={places} min={minPlaces} max={32} step={step} label={t().placesLabel} onChange={setPlaces} />
          ) : (
            <div>
              <b>{data.max_players}</b> {locked}
            </div>
          )}
          {data.kind === 'pairs' && (
            <>
              <label>{t().scoreModeLabel}</label>
              <div className="seg full" aria-disabled={!before}>
                {(['games', 'points'] as ScoreMode[]).map((m) => (
                  <button
                    key={m}
                    disabled={!before}
                    aria-pressed={mode === m}
                    onClick={() => {
                      setMode(m)
                      setPoints(m === 'points' ? 21 : 6)
                    }}
                  >
                    {m === 'points' ? t().scorePoints : t().scoreGames}
                  </button>
                ))}
              </div>
            </>
          )}
          {king ? (
            <>
              <label>{t().roundMinutesLabel}</label>
              <PresetNumber
                value={roundMinutes}
                presets={[10, 12, 15]}
                min={3}
                max={60}
                unit={t().minutes}
                label={t().roundMinutesLabel}
                onChange={setRoundMinutes}
              />
            </>
          ) : (
            <>
              <label>{t().matchTo}</label>
              <PresetNumber
                value={points}
                presets={pointsMode ? [16, 21, 24, 32] : [4, 6, 9]}
                min={1}
                max={99}
                unit={pointsMode ? t().pointsUnit : t().gamesUnit}
                label={t().matchTo}
                onChange={setPoints}
              />
            </>
          )}
          {(isIndividualKind(data.kind) || king) && (
            <>
              <label>{t().roundsLabel}</label>
              {roundsEditable ? (
                <PresetNumber
                  value={rounds}
                  presets={mex ? [4, 6, 8] : [5, 7, 9]}
                  min={mex || king ? Math.max(1, data.rounds_total) : 1}
                  max={64}
                  allowAuto={!king}
                  allowUnlimited={mex || king}
                  label={t().roundsLabel}
                  onChange={setRounds}
                />
              ) : (
                locked
              )}
            </>
          )}
          {!king && (
            <>
              <label>{t().courtsLabel}</label>
              {before ? (
                <PresetNumber value={courts} presets={[1, 2, 3, 4]} min={1} max={12} allowAuto label={t().courtsLabel} onChange={setCourts} />
              ) : (
                locked
              )}
            </>
          )}

          <div className="muted small" style={{ marginTop: 14 }}>
            {t().editNotifyHint}
          </div>
          <button style={{ width: '100%', marginTop: 12 }} disabled={busy || !dirty} onClick={save}>
            {t().save}
          </button>
        </div>
      </div>
    </div>
  )
}
